/* ============================================================================
   OXBLOOD — behaviour
   ----------------------------------------------------------------------------
   Vanilla, no deps, idempotent. Everything is opt-in via data attributes so a
   page only gets the behaviour it marks up. Import once, at the end of <body>.
   ============================================================================ */

(() => {
  "use strict";

  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* -- Marquee ---------------------------------------------------------------
     Cloning once and shifting -50% only loops seamlessly when the original run
     is already wider than the viewport. When it is not — a short row of logos
     on a wide screen — the shift outruns the content and the strip visibly
     tears mid-scroll. So: measure one run, clone until the track covers the
     container twice over, and shift by exactly ONE run width. After that shift
     the next copy sits precisely where the first began, so the seam never
     exists at any width. Duration is derived from distance to hold a constant
     speed rather than a constant lap time. */
  const PX_PER_SEC = 55;

  const layoutMarquee = (track) => {
    const originals = track.dataset.oxOriginals
      ? [...track.children].slice(0, +track.dataset.oxOriginals)
      : [...track.children];

    if (!track.dataset.oxOriginals) {
      track.dataset.oxOriginals = String(originals.length);
    } else {
      [...track.children].slice(originals.length).forEach((n) => n.remove());
    }
    if (!originals.length) return;

    const runWidth = originals.reduce((w, el) => {
      const cs = getComputedStyle(el);
      return w + el.getBoundingClientRect().width +
        parseFloat(cs.marginLeft || 0) + parseFloat(cs.marginRight || 0);
    }, 0);
    if (runWidth <= 0) return;

    const need = (track.parentElement?.getBoundingClientRect().width || runWidth) * 2 + runWidth;
    const copies = Math.max(1, Math.ceil(need / runWidth) - 1);
    const frag = document.createDocumentFragment();
    for (let i = 0; i < copies; i++) {
      originals.forEach((el) => frag.appendChild(el.cloneNode(true)));
    }
    track.appendChild(frag);

    track.style.setProperty("--ox-marquee-shift", runWidth + "px");
    track.style.animationDuration = (runWidth / PX_PER_SEC).toFixed(2) + "s";
  };

  const initMarquee = () => {
    const tracks = [...document.querySelectorAll("[data-ox-marquee]")];
    if (!tracks.length) return;
    tracks.forEach(layoutMarquee);

    /* Re-measure on resize: the run width changes with breakpoint padding, and
       a stale shift value reintroduces the tear. */
    let t = 0;
    addEventListener("resize", () => {
      clearTimeout(t);
      t = setTimeout(() => tracks.forEach(layoutMarquee), 180);
    });

    /* Images arrive after first layout and change the run width. */
    tracks.forEach((track) => {
      track.querySelectorAll("img").forEach((img) => {
        if (!img.complete) img.addEventListener("load", () => layoutMarquee(track), { once: true });
      });
    });
  };

  /* -- Roving radiogroup ----------------------------------------------------
     ARIA expects a radiogroup to hold ONE tab stop, with arrow keys moving
     between options. Wires that up for any set of role="radio" elements and
     calls onSelect(index) whenever the choice changes. */
  const rovingRadios = (items, onSelect) => {
    const select = (i, focus) => {
      items.forEach((el, j) => {
        const on = i === j;
        el.setAttribute("aria-checked", String(on));
        el.tabIndex = on ? 0 : -1;
      });
      if (focus) items[i].focus();
      onSelect(i);
    };

    const checked = items.findIndex((el) => el.getAttribute("aria-checked") === "true");
    items.forEach((el, i) => {
      el.tabIndex = i === (checked < 0 ? 0 : checked) ? 0 : -1;
      el.addEventListener("click", () => select(i, false));
      el.addEventListener("keydown", (e) => {
        const back = e.key === "ArrowUp" || e.key === "ArrowLeft";
        const fwd = e.key === "ArrowDown" || e.key === "ArrowRight";
        if (back || fwd) {
          e.preventDefault();
          select((i + (fwd ? 1 : -1) + items.length) % items.length, true);
        } else if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          select(i, false);
        }
      });
    });
    return select;
  };

  /* -- Offer selector: segmented toggle + single-select cards --------------- */
  const initOffer = () => {
    document.querySelectorAll("[data-ox-offer]").forEach((offer) => {
      const toggle = offer.querySelector(".ox-offer__toggle");
      const modes = [...offer.querySelectorAll(".ox-offer__toggle-btn")];
      const cards = [...offer.querySelectorAll(".ox-offer-card")];
      const cta = offer.querySelector("[data-ox-offer-cta]");

      const priceOf = (card) =>
        card.dataset[toggle?.dataset.active === "1" ? "priceSub" : "priceOnce"] ||
        card.dataset.priceOnce ||
        "";

      /* The CTA verb is the page's decision, not the library's. Author it with
         data-ox-cta-label; {price} is substituted. Defaults to "Add to cart". */
      const syncCta = () => {
        const active = cards.find((c) => c.getAttribute("aria-checked") === "true");
        if (!cta || !active) return;
        const tpl = cta.dataset.oxCtaLabel || "Add to cart · {price}";
        cta.textContent = tpl.replace("{price}", priceOf(active));
      };

      /* Everything the buyer can see about a tier repaints from the same pass.
         Price, per-unit line and savings badge all change between one-time and
         subscribe, so painting only the price leaves the card contradicting
         itself — a $127 total sitting above "$50 a bottle. Save $45."
         The sticky bar mirrors the same state: it is a second view of one
         decision, and a bar that disagrees with the buy box reads as a pricing
         trick to exactly the buyer this page is written for. */
      const sticky = document.querySelector("[data-ox-sticky-atc]");

      const paint = () => {
        const sub = toggle?.dataset.active === "1";
        cards.forEach((card) => {
          const price = priceOf(card);
          const el = card.querySelector(".ox-offer-card__price");
          if (el && price) el.textContent = price;

          const meta = card.querySelector(".ox-offer-card__meta");
          const metaText = sub ? card.dataset.metaSub : card.dataset.metaOnce;
          if (meta && metaText) meta.textContent = metaText;

          const badge = card.querySelector(".ox-offer-card__badge");
          const badgeText = sub ? card.dataset.badgeSub : card.dataset.badgeOnce;
          if (badge && badgeText) badge.textContent = badgeText;

          const legacy = card.querySelector("[data-ox-meta-sub]");
          if (legacy) legacy.hidden = !sub;
        });

        const active = cards.find((c) => c.getAttribute("aria-checked") === "true");
        if (sticky && active) {
          const t = sticky.querySelector("[data-ox-sticky-title]");
          const p = sticky.querySelector("[data-ox-sticky-price]");
          if (t) t.textContent = active.querySelector(".ox-offer-card__title").textContent;
          if (p) p.textContent = priceOf(active) + (active.dataset.stickySuffix || (sub ? " · ships every cycle" : " · one time"));
        }

        syncCta();
      };

      rovingRadios(modes, (i) => {
        if (toggle) toggle.dataset.active = String(i);
        paint();
      });

      /* paint(), not syncCta(): choosing a tier has to repaint the sticky bar
         too, and syncCta only knows about the button. */
      rovingRadios(cards, paint);

      paint();
    });
  };

  /* -- Timeline chips ------------------------------------------------------ */
  const initTimeline = () => {
    document.querySelectorAll("[data-ox-timeline]").forEach((group) => {
      const chips = [...group.querySelectorAll(".ox-timeline__chip")];
      const panels = [...document.querySelectorAll(`[data-ox-timeline-panel="${group.dataset.oxTimeline}"]`)];
      chips.forEach((chip, i) => {
        chip.addEventListener("click", () => {
          chips.forEach((c, j) => c.setAttribute("aria-selected", String(i === j)));
          panels.forEach((p, j) => { p.hidden = i !== j; });
        });
      });
    });
  };

  /* -- Reveal + counters: one IntersectionObserver, fires once per element -- */
  const initObserved = () => {
    const targets = document.querySelectorAll(".ox-reveal, [data-ox-count]");
    if (!targets.length) return;

    const runCount = (el) => {
      const to = parseFloat(el.dataset.oxCount);
      const suffix = el.dataset.oxCountSuffix || "";
      if (reduced) { el.textContent = to + suffix; return; }
      const dur = 1400;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(to * eased) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.classList.contains("ox-reveal")) el.dataset.revealed = "true";
          if (el.dataset.oxCount && el.dataset.oxCounted !== "true") {
            el.dataset.oxCounted = "true";
            runCount(el);
          }
          io.unobserve(el);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );

    targets.forEach((t) => io.observe(t));
  };

  /* -- Sticky ATC: show once the inline buy box has scrolled past ----------- */
  const initStickyAtc = () => {
    const bar = document.querySelector("[data-ox-sticky-atc]");
    const anchor = document.querySelector("[data-ox-sticky-anchor]");
    if (!bar || !anchor) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        bar.dataset.visible = String(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  };

  /* -- Foil budget audit ----------------------------------------------------
     Foil is a scarcity effect: it survives only while it is rare. Nothing here
     changes rendering — it just refuses to let the budget drift silently as a
     page grows. Add data-ox-foil-exempt to a container to skip it (specimen
     sheets, this system's own showcase). */
  const auditFoil = () => {
    const foils = [...document.querySelectorAll(".ox-foil, .ox-wordmark")]
      .filter((el) => !el.closest("[data-ox-foil-exempt]"));
    if (!foils.length) return;

    if (foils.length > 3) {
      console.warn(
        `[oxblood] ${foils.length} foiled elements on this page. Budget is 3; two is better, one is best. Past that it stops reading as a stamp and becomes the heading color.`
      );
    }

    const perBand = new Map();
    foils.forEach((el) => {
      const band = el.closest(".ox-band");
      if (band) perBand.set(band, (perBand.get(band) || 0) + 1);
    });
    perBand.forEach((n, band) => {
      if (n > 1) {
        console.warn(
          `[oxblood] ${n} foiled elements in one band (${band.id ? "#" + band.id : "unnamed"}). One per band — never two in the same viewport.`,
          band
        );
      }
    });

    foils.forEach((el) => {
      if (!el.closest(".ox-band--void")) {
        console.warn("[oxblood] foil outside a void band — it needs a dark ground to reflect. Rendering inert.", el);
        return;
      }
      const size = parseFloat(getComputedStyle(el).fontSize);
      if (size < 40) {
        console.warn(
          `[oxblood] foil at ${Math.round(size)}px — below the 40px floor the ramp collapses and the dark shoulders fall under contrast.`,
          el
        );
      }
    });
  };

  const init = () => {
    initMarquee();
    initOffer();
    initTimeline();
    initObserved();
    initStickyAtc();
    auditFoil();
  };

  /* Re-run the budget audit after foil is added or removed at runtime. */
  window.oxAuditFoil = auditFoil;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
